import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import ImageWithFallback from './ImageWithFallback';
import ShareButton from './ShareButton';
import * as FiIcons from 'react-icons/fi';

const {
  FiX,
  FiStar,
  FiUser,
  FiCalendar,
  FiMapPin,
  FiFacebook,
  FiInstagram,
  FiGlobe,
  FiArrowRight
} = FiIcons;

const PlayerModal = ({ player, isOpen, onClose }) => {
  if (!isOpen || !player) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const hasSocialLinks = player.facebook_url || player.instagram_url || player.website_url;
  const profilePath = `/team/player/${player.id}`;
  const shareUrl = `${window.location.origin}/share/player/${player.id}`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative bg-primary-600 text-white p-6 rounded-t-2xl">
          <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>

          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 bg-white bg-opacity-20 rounded-full overflow-hidden flex-shrink-0">
              <ImageWithFallback src={player.image_url} alt={player.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-1">{player.name}</h2>
              <p className="text-primary-200 text-lg">{player.position}</p>
              <div className="flex items-center space-x-4 mt-2">
                {player.number && <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm font-semibold">#{player.number}</span>}
                {player.age && <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-sm font-semibold">{player.age} ετών</span>}
              </div>
            </div>
          </div>

          {player.is_featured && (
            <div className="absolute top-4 left-4">
              <div className="bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold flex items-center">
                <SafeIcon icon={FiStar} className="w-3 h-3 mr-1" />
                {player.featured_label || 'ΤΑΛΕΝΤΟ'}
              </div>
            </div>
          )}
        </div>

        <div className="p-6">
          <div className="flex justify-center mb-6">
            <div className="w-48 h-64 bg-gray-200 rounded-lg overflow-hidden">
              <ImageWithFallback src={player.image_url} alt={player.name} className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <SafeIcon icon={FiUser} className="w-5 h-5 text-primary-600" />
                <div><p className="text-sm text-gray-500">Θέση</p><p className="font-semibold">{player.position}</p></div>
              </div>
              {player.age && (
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiCalendar} className="w-5 h-5 text-primary-600" />
                  <div><p className="text-sm text-gray-500">Ηλικία</p><p className="font-semibold">{player.age} ετών</p></div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {player.number && (
                <div className="flex items-center space-x-3">
                  <div className="w-5 h-5 bg-primary-600 rounded text-white flex items-center justify-center text-xs font-bold">#</div>
                  <div><p className="text-sm text-gray-500">Αριθμός Φανέλας</p><p className="font-semibold">#{player.number}</p></div>
                </div>
              )}
              {player.team_categories && player.team_categories.length > 0 && (
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5 text-primary-600 mt-1" />
                  <div>
                    <p className="text-sm text-gray-500">Κατηγορίες</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {player.team_categories.map((category) => <span key={category} className="bg-primary-100 text-primary-800 px-2 py-1 rounded-full text-xs font-medium">{category}</span>)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {player.bio && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Βιογραφικό</h3>
              <p className="text-gray-700 leading-relaxed">{player.bio}</p>
            </div>
          )}

          {hasSocialLinks && (
            <div className="mb-6 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-500 mb-3 text-center">Social Media</h3>
              <div className="flex justify-center space-x-4">
                {player.facebook_url && <a href={player.facebook_url} target="_blank" rel="noopener noreferrer" className="p-2 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors" title="Facebook"><SafeIcon icon={FiFacebook} className="w-6 h-6" /></a>}
                {player.instagram_url && <a href={player.instagram_url} target="_blank" rel="noopener noreferrer" className="p-2 bg-pink-100 text-pink-600 rounded-full hover:bg-pink-200 transition-colors" title="Instagram"><SafeIcon icon={FiInstagram} className="w-6 h-6" /></a>}
                {player.website_url && <a href={player.website_url} target="_blank" rel="noopener noreferrer" className="p-2 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 transition-colors" title="Website"><SafeIcon icon={FiGlobe} className="w-6 h-6" /></a>}
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row justify-center gap-3 pt-4 border-t">
            <Link to={profilePath} onClick={onClose} className="inline-flex items-center justify-center rounded-xl bg-primary-600 px-5 py-3 font-bold text-white hover:bg-primary-700">
              Πλήρες προφίλ <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4" />
            </Link>
            <ShareButton
              url={shareUrl}
              title={`${player.name} - Α.Ο. Χανιά WFC`}
              description={`${player.position}${player.age ? ` - ${player.age} ετών` : ''}. ${player.bio || ''}`}
              imageUrl={player.image_url || ''}
              buttonText="Κοινοποίηση Παίκτριας"
              variant="outline"
            />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default PlayerModal;
